import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, TextField, Button, Card, CardContent, Avatar, Divider, Stack } from '@mui/material';
import { apiListCoursePosts, apiCreateCoursePost, apiAddComment } from '../../services/api';
import { Post, Comment as CommentType } from '../../types';

// Gradient-initial avatar treatment, matching the Home feed look (ember spark).
const AVATAR_GRAD = 'linear-gradient(140deg,#FFC24B 0%,#FF7A36 60%,#FF5B2E 100%)';
const gradAvatarSx = { background: AVATAR_GRAD, color: '#fff', fontWeight: 700 } as const;

const CoursePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [content, setContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState<Record<string, string>>({}); // by postId
  const [replyText, setReplyText] = useState<Record<string, string>>({}); // by commentId

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!id) return;
      try {
        setLoading(true);
        const resp: any = await apiListCoursePosts(id);
        if (!alive) return;
        setPosts(((resp?.items as any[]) || []).map(shapeFromDto));
        setCursor(resp?.nextCursor || null);
      } catch (e: any) {
        if (!alive) return;
        setError(e?.message || 'Failed to load');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  function shapeFromDto(p: any): Post {
    return {
      id: p.id,
      authorId: p.authorId,
      author: (p.author as any) || ({ id: p.authorId, displayName: 'Unknown Student', profileImage: null } as any),
      content: p.content,
      likes: Array.isArray(p.likes) ? p.likes : [],
      comments: Array.isArray(p.comments) ? p.comments.map((c: any) => ({
        id: c.id,
        content: c.content,
        parentId: c.parentId ?? null,
        createdAt: new Date(c.createdAt),
        author: c.author,
        children: (c.children || []).map((d: any) => ({
          id: d.id,
          content: d.content,
          parentId: d.parentId ?? null,
          createdAt: new Date(d.createdAt),
          author: d.author,
          children: [],
        }))
      })) : [],
      courseId: p.courseId || undefined,
      tags: [],
      createdAt: new Date(p.createdAt),
      updatedAt: new Date(p.updatedAt),
    };
  }

  function shapeCommentFromDto(c: any): CommentType {
    return {
      id: c.id,
      content: c.content,
      parentId: c.parentId ?? null,
      createdAt: new Date(c.createdAt),
      author: c.author,
      children: (c.children || []).map(shapeCommentFromDto),
    } as CommentType;
  }

  async function handleCreate() {
    if (!id || !content.trim()) return;
    const optimistic: Post = {
      id: `tmp-${Date.now()}`,
      authorId: '',
      author: ({ id: '', displayName: 'You', profileImage: null } as any),
      content,
      likes: [],
      comments: [],
      courseId: id,
      tags: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    setPosts(prev => [optimistic, ...prev]);
    setContent('');
    try {
      const created: any = await apiCreateCoursePost(id, content);
      setPosts(prev => [shapeFromDto(created), ...prev.filter(p => p.id !== optimistic.id)]);
    } catch (_) {
      setPosts(prev => prev.filter(p => p.id !== optimistic.id));
    }
  }

  async function handleAddComment(postId: string) {
    const text = (newComment[postId] || '').trim();
    if (!text) return;
    setNewComment(prev => ({ ...prev, [postId]: '' }));
    try {
      const saved: any = await apiAddComment(postId, { content: text });
      const shaped = shapeCommentFromDto(saved);
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments: [...p.comments, shaped] } : p));
    } catch (e) {
      // restore text on failure
      setNewComment(prev => ({ ...prev, [postId]: text }));
    }
  }

  async function handleReply(postId: string, parentId: string) {
    const text = (replyText[parentId] || '').trim();
    if (!text) return;
    setReplyText(prev => ({ ...prev, [parentId]: '' }));
    try {
      const saved: any = await apiAddComment(postId, { content: text, parentId });
      const shaped = shapeCommentFromDto(saved);
      setPosts(prev => prev.map(p => p.id === postId ? {
        ...p,
        comments: p.comments.map(c => c.id === parentId ? { ...c, children: [...(c.children || []), shaped] } : c)
      } : p));
    } catch (e) {
      setReplyText(prev => ({ ...prev, [parentId]: text }));
    }
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" component="div" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>Course Discussion</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }} component="div">
        Share questions and updates with your classmates.
      </Typography>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <TextField fullWidth multiline minRows={3} placeholder="Share something with the class" value={content} onChange={e => setContent(e.target.value)} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button variant="contained" disabled={!content.trim()} onClick={handleCreate}>Post</Button>
          </Box>
        </CardContent>
      </Card>
      {posts.map((p, idx) => (
        <React.Fragment key={p.id}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                <Avatar src={(p.author as any).profileImage || undefined} sx={gradAvatarSx}>{(p.author as any).displayName?.[0]}</Avatar>
                <Typography variant="subtitle2" component="div" sx={{ fontWeight: 700, color: 'primary.main' }}>{(p.author as any).displayName}</Typography>
                <Typography variant="body2" color="text.secondary" component="div">{p.createdAt.toLocaleString()}</Typography>
              </Box>
              <Typography component="div">{p.content}</Typography>

              {/* Comments */}
              <Box sx={{ mt: 2 }}>
                {(p.comments || []).map(c => (
                  <Box key={c.id} sx={{ pl: 1, borderLeft: '2px solid', borderColor: 'divider', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: 12, ...gradAvatarSx }} src={(c.author as any).profileImage || undefined}>{(c.author as any).displayName?.[0]}</Avatar>
                      <Typography variant="body2" component="div">{(c.author as any).displayName}</Typography>
                      <Typography variant="caption" color="text.secondary" component="div">{c.createdAt.toLocaleString()}</Typography>
                    </Box>
                    <Typography variant="body2" component="div" sx={{ ml: 4 }}>{c.content}</Typography>
                    {/* Children */}
                    {(c.children || []).map(ch => (
                      <Box key={ch.id} sx={{ pl: 2, ml: 4, borderLeft: '2px dashed', borderColor: 'divider', mt: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 22, height: 22, fontSize: 11, ...gradAvatarSx }} src={(ch.author as any).profileImage || undefined}>{(ch.author as any).displayName?.[0]}</Avatar>
                          <Typography variant="body2" component="div">{(ch.author as any).displayName}</Typography>
                          <Typography variant="caption" color="text.secondary" component="div">{ch.createdAt.toLocaleString()}</Typography>
                        </Box>
                        <Typography variant="body2" component="div" sx={{ ml: 4 }}>{ch.content}</Typography>
                      </Box>
                    ))}
                    <Stack direction="row" spacing={1} sx={{ mt: 1, ml: 4 }}>
                      <TextField size="small" fullWidth placeholder="Reply…" value={replyText[c.id] || ''} onChange={e => setReplyText(prev => ({ ...prev, [c.id]: e.target.value }))} />
                      <Button variant="outlined" onClick={() => handleReply(p.id, c.id)} disabled={!replyText[c.id]?.trim()}>Reply</Button>
                    </Stack>
                  </Box>
                ))}
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <TextField size="small" fullWidth placeholder="Add a comment…" value={newComment[p.id] || ''} onChange={e => setNewComment(prev => ({ ...prev, [p.id]: e.target.value }))} />
                  <Button variant="contained" onClick={() => handleAddComment(p.id)} disabled={!newComment[p.id]?.trim()}>Comment</Button>
                </Stack>
              </Box>
            </CardContent>
          </Card>
          {idx < posts.length - 1 && <Divider />}
        </React.Fragment>
      ))}
      {!loading && cursor && (
        <Box sx={{ textAlign: 'center', my: 2 }}>
          <Button onClick={async () => {
            if (!id || !cursor) return;
            try {
              const resp: any = await apiListCoursePosts(id, cursor);
              setPosts(prev => [...prev, ...((resp.items || []).map(shapeFromDto))]);
              setCursor(resp.nextCursor || null);
            } catch (e) {
              console.error('load more course posts failed', e);
            }
          }}>Load more</Button>
        </Box>
      )}
    </Box>
  );
};

export default CoursePage;
